import React, { useContext, useEffect, useMemo, useState } from "react";

import { faSpinner } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faCheck, faExclamationTriangle } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";

import AppContext from "~/common/AppContext";
import LocalRecordingManager, { AmplitudeEvent } from "~/localRecording/LocalRecordingManager";
import { useRecordingState } from "~/localRecording/useRecordingState";
import type { CopilotStatus as CopilotStatusType } from "~/stream/MeetingPhaseContext";
import { Tooltip, type TooltipProps } from "~/ui";

import OnAirIcon from "../OnAirIcon";
import PauseRecordingButton from "./PauseRecordingButton";
import { ControlsGroup, ControlsGroupItem } from "./SharedComponents";
import StopRecordingButton from "./StopRecordingButton";
import ToggleMuteButton from "./ToggleMuteButton";
import WaveformIndicator from "./WaveformIndicator";

import styles from "./InMeetingControls.module.css";

export interface InMeetingControlsProps {
	copilotStatus: Exclude<CopilotStatusType, { type: "not-recording" } | { type: "scheduled" }>;
	creator?: { id: string; firstName: string } | null;
}

type InMeetingControlsCopilotStatus = InMeetingControlsProps["copilotStatus"]["type"];

function LoadingStatus({ children }: { children: React.ReactNode }) {
	return (
		<ControlsGroupItem>
			<FontAwesomeIcon icon={faSpinner} fixedWidth pulse className={styles.statusIcon} />
			{children}
		</ControlsGroupItem>
	);
}

export default function InMeetingControls({ copilotStatus, creator }: InMeetingControlsProps) {
	const { user } = useContext(AppContext);
	// A simple ring-buffer of the last five amplitude samples (as percentages 0–100).
	const [amplitudes, setAmplitudes] = useState<number[]>([15, 15, 15, 15, 15]);
	const [showConsentMessage, setShowConsentMessage] = useState(false);

	const { isActive: isLocalRecordingActive } = useRecordingState();

	const isClientSideProvider = "provider" in copilotStatus && copilotStatus.provider === "CLIENT_SIDE";
	const isClientSideRecordingActive = isClientSideProvider && window.DesktopRecorderAPI && isLocalRecordingActive;

	// Show consent message for first 5 seconds after recording started (botless mode only)
	useEffect(() => {
		if (
			copilotStatus.type === "recording" &&
			isClientSideProvider &&
			"statusCreatedAt" in copilotStatus &&
			copilotStatus.statusCreatedAt
		) {
			const now = Date.now();
			const startTime = new Date(copilotStatus.statusCreatedAt).getTime();
			const elapsedMs = now - startTime;
			const remainingMs = 5000 - elapsedMs;

			if (remainingMs > 0) {
				// Still within the first 5 seconds
				setShowConsentMessage(true);
				const timer = setTimeout(() => {
					setShowConsentMessage(false);
				}, remainingMs);

				return () => clearTimeout(timer);
			} else {
				// Already past 5 seconds
				setShowConsentMessage(false);
			}
		} else {
			setShowConsentMessage(false);
		}
		return;
	}, [copilotStatus, isClientSideProvider]);

	// Listen for microphone amplitude updates when using client-side recording.
	useEffect(() => {
		if (copilotStatus.type !== "recording") {
			return;
		}
		if (!isClientSideRecordingActive) {
			return;
		}

		const handleAmplitude = (ev: AmplitudeEvent) => {
			const pct = Math.max(15, Math.min(100, ev.amplitude * 100));
			setAmplitudes(prev => [...prev.slice(1), pct]);
		};

		// Register listener with desktop preload API if available.
		const unregister = () => {
			LocalRecordingManager.removeEventListener("amplitude", handleAmplitude);
		};

		LocalRecordingManager.addEventListener("amplitude", handleAmplitude);

		return unregister;
	}, [copilotStatus, isClientSideRecordingActive]);

	const status: { [key in InMeetingControlsCopilotStatus]: TooltipProps["children"] } = useMemo(
		() => ({
			starting: <LoadingStatus>Joining…</LoadingStatus>,
			waiting: <LoadingStatus>Waiting…</LoadingStatus>,
			stopping: <LoadingStatus>Saving…</LoadingStatus>,
			recording: (
				<ControlsGroupItem className={styles.recordingStatus}>
					<OnAirIcon className={styles.statusIcon} />
					{isClientSideRecordingActive ? (
						<WaveformIndicator amplitudes={amplitudes} className={styles.statusIcon} />
					) : (
						"REC"
					)}
				</ControlsGroupItem>
			),
			paused: <ControlsGroupItem>Paused</ControlsGroupItem>,
			saved: (
				<ControlsGroupItem className={styles.savedStatus}>
					<FontAwesomeIcon icon={faCheck} className={styles.statusIcon} />
					Saved
				</ControlsGroupItem>
			),
			error:
				copilotStatus.type === "error" && copilotStatus.indicator.type === "cancelled" ? (
					<ControlsGroupItem>Cancelled</ControlsGroupItem>
				) : (
					<ControlsGroupItem className={styles.errorStatus}>
						<FontAwesomeIcon icon={faExclamationTriangle} className={styles.statusIcon} />
						Error
					</ControlsGroupItem>
				),
		}),
		[copilotStatus, amplitudes, isClientSideRecordingActive],
	);

	const tooltipContents: { [key in InMeetingControlsCopilotStatus]?: TooltipProps["content"] } = useMemo(
		() => ({
			starting: "The Note Taker will soon ask to join your call.",
			waiting: "Allow the Note Taker into your meeting from your video call provider.",
			// undefined will disable the tooltip
			error: copilotStatus.type === "error" ? copilotStatus.indicator.message : undefined,
		}),
		[copilotStatus],
	);

	const shouldShowToggleMute = useMemo(() => {
		return (
			(copilotStatus.type === "recording" || copilotStatus.type === "paused") &&
			isClientSideProvider &&
			user?.id &&
			creator?.id === user.id &&
			window.DesktopRecorderAPI
		);
	}, [copilotStatus, creator?.id, user?.id, isClientSideProvider]);

	const canStop =
		copilotStatus.type === "starting" ||
		copilotStatus.type === "waiting" ||
		copilotStatus.type === "recording" ||
		copilotStatus.type === "paused";

	const canPause = copilotStatus.type === "recording" || copilotStatus.type === "paused";
	const smallRecordingLabel = useMemo(() => {
		if (copilotStatus.type !== "recording") return null;

		// Show consent message for first 5 seconds after recording started
		if (showConsentMessage) {
			return <small className={styles.localRecordingLabel}>Always get consent when recording.</small>;
		}

		const mediaType = copilotStatus.mediaType === "VIDEO" ? "audio and video" : "audio";
		let actorAndVerb: string;
		if (isClientSideProvider) {
			if (user?.id && creator?.id === user.id) {
				actorAndVerb = "You're recording";
			} else if (creator?.firstName) {
				actorAndVerb = `${creator.firstName} is recording`;
			} else {
				actorAndVerb = "Capturing";
			}
		} else {
			actorAndVerb = "Capturing";
		}
		return (
			<small className={styles.localRecordingLabel}>
				{actorAndVerb} {mediaType}.
			</small>
		);
	}, [copilotStatus, user?.id, creator?.id, creator?.firstName, showConsentMessage, isClientSideProvider]);

	return (
		<div
			className={cx(styles.wrapper, {
				[styles.showSmallLabelOnHover]: !user?.id || user.id !== creator?.id,
			})}
		>
			{smallRecordingLabel}
			<ControlsGroup className={cx({ [styles.pausedControlsGroup]: copilotStatus.type === "paused" })}>
				<Tooltip
					content={tooltipContents[copilotStatus.type]}
					disabled={!tooltipContents[copilotStatus.type]}
					placement="top"
					maxWidth={150}
					className={styles.tooltip}
					delay={[500, 0]}
					offset={[0, 16]}
				>
					{status[copilotStatus.type]}
				</Tooltip>
				{(canPause || canStop) && <div className={styles.divider} />}
				{canPause && <PauseRecordingButton copilotStatus={copilotStatus.type} />}
				{canStop && <StopRecordingButton copilotStatus={copilotStatus.type} />}
				{shouldShowToggleMute && (
					<>
						<div className={styles.divider} /> <ToggleMuteButton />
					</>
				)}
			</ControlsGroup>
		</div>
	);
}
